import { ChangeEvent, forwardRef, memo, useCallback, useId } from 'react';
import { useControlledState } from '../_core/hooks/use-controlled-state';
import { useFieldRef } from '../_core/hooks/use-field-ref';
import { TextareaProps } from './textarea.types';
import {
  AutoSizer,
  Mirror,
  Root,
  StyledTextarea,
  TextareaFooter,
  TextareaWrapper,
} from './textarea.styled';
import { CharCounter } from '../_core/field';

export const Textarea = memo(
  forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
    const {
      size = 's',
      status,
      autoResize = false,
      maxCount,
      disabled,
      readOnly,
      id,
      value,
      defaultValue,
      onChange,
      style,
      className,
      ...restProps
    } = props;

    const [currentValue, setCurrentValue] = useControlledState(
      value === undefined ? undefined : String(value),
      String(defaultValue ?? '')
    );

    const counterId = useId();

    const { elementRef: textareaRef, attachRef } =
      useFieldRef<HTMLTextAreaElement>(ref, (node) =>
        setCurrentValue(node.value)
      );

    const charCount = currentValue.length;
    const isExceeded = maxCount !== undefined && charCount > maxCount;
    const showFooter = maxCount !== undefined;

    const ariaProps = {
      id,
      'aria-invalid': status === 'error' || isExceeded || undefined,
      'aria-describedby': showFooter ? counterId : undefined,
    };

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        setCurrentValue(event.target.value);
        onChange?.(event);
      },
      [setCurrentValue, onChange]
    );

    return (
      <Root style={style} className={className}>
        <TextareaWrapper
          $size={size}
          $status={status}
          data-disabled={disabled ? '' : undefined}
          data-readonly={readOnly ? '' : undefined}
          onClick={() => !disabled && textareaRef.current?.focus()}
        >
          <AutoSizer>
            {autoResize && (
              <Mirror $size={size} aria-hidden="true">
                {/* Хвостовой пробел — чтобы перевод строки в конце тоже давал строку */}
                {currentValue + ' '}
              </Mirror>
            )}

            <StyledTextarea
              ref={attachRef}
              $size={size}
              $status={status}
              $autoResize={autoResize}
              disabled={disabled}
              readOnly={readOnly}
              {...ariaProps}
              value={value}
              defaultValue={defaultValue}
              onChange={handleChange}
              {...restProps}
            />
          </AutoSizer>
        </TextareaWrapper>

        {showFooter && (
          <TextareaFooter>
            <CharCounter id={counterId} $exceeded={isExceeded}>
              {charCount}/{maxCount}
            </CharCounter>
          </TextareaFooter>
        )}
      </Root>
    );
  })
);

Textarea.displayName = 'Textarea';
